import { Controller } from "@hotwired/stimulus"

const MODAL_PARAMS = {
  attachment: "modal_attachment",
  task: "modal_task",
  contact: "modal_contact"
}
let pendingCloseRestore = null

// This listener is installed before Turbo starts (see application.js). A
// popstate listener added when the dialog closes would run after Turbo's own
// listener, too late to prevent a restore of the old modal URL.
if (typeof window !== "undefined") {
  window.addEventListener("popstate", () => {
    const pending = pendingCloseRestore
    if (!pending) return

    pendingCloseRestore = null
    window.clearTimeout(pending.timeout)

    const url = new URL(window.location.href)
    Object.values(MODAL_PARAMS).forEach(param => url.searchParams.delete(param))
    if (url.href !== pending.href || url.href === window.location.href) return

    const state = { ...(window.history.state || {}) }
    delete state.modal
    delete state.modalStackDepth
    window.history.replaceState(state, "", url.href)
  })
}

// Persistent controller (mounted on the layout's <main>) that keeps the shared
// #modal turbo-frame in sync with URL modal params across browser back/forward.
//
// The frame_modal controller pushes a real page URL when a modal opens, but it
// can't react to popstate — it's unmounted whenever the modal is closed, so it
// can't catch a "forward to reopen" navigation. This router lives on the
// always-present layout, listens for popstate, and drives the modal frame from
// the URL: load the modal when the param appears, clear it when the param is
// gone. Every history entry is a real page URL, so this can never dead-end.
export default class extends Controller {
  connect() {
    this.boundSync = this.syncFromUrl.bind(this)
    this.boundCloseStack = this.closeStack.bind(this)
    this.boundBeforeOpen = this.suppressStaleOpen.bind(this)
    this.boundRequestOpen = this.allowRequestedOpen.bind(this)
    this.boundBeforeRender = this.syncModalInRestoredPage.bind(this)
    window.addEventListener("popstate", this.boundSync)
    window.addEventListener("modal-router:close-stack", this.boundCloseStack)
    window.addEventListener("modal-router:before-open", this.boundBeforeOpen)
    document.addEventListener("click", this.boundRequestOpen, true)
    document.addEventListener("turbo:before-render", this.boundBeforeRender)
  }

  disconnect() {
    window.removeEventListener("popstate", this.boundSync)
    window.removeEventListener("modal-router:close-stack", this.boundCloseStack)
    window.removeEventListener("modal-router:before-open", this.boundBeforeOpen)
    document.removeEventListener("click", this.boundRequestOpen, true)
    document.removeEventListener("turbo:before-render", this.boundBeforeRender)
  }

  get frame() {
    return document.getElementById("modal")
  }

  // Modal currently rendered inside the shared frame (set by frame-modal), or
  // null when empty/closed.
  get currentModal() {
    const el = this.frame?.querySelector("[data-frame-modal-id-value], [data-frame-modal-attachment-id-value]")
    if (!el) return null

    return {
      type: el.dataset.frameModalTypeValue || "attachment",
      id: el.dataset.frameModalIdValue || el.dataset.frameModalAttachmentIdValue || ""
    }
  }

  syncFromUrl() {
    const frame = this.frame
    if (!frame) return

    const modal = this.modalFromUrl()
    const current = this.currentModal

    if (modal) {
      if (!current || modal.type !== current.type || modal.id !== current.id) {
        // Navigated (back/forward) to a modal URL that isn't open — load it.
        // The reader asked for it, so a recent close must not swallow it.
        this.suppressedModal = null
        frame.removeAttribute("complete")
        frame.src = this.srcForModal(modal)
      }
    } else if (current || frame.innerHTML.trim() !== "") {
      // Navigated away from any modal URL — clear/close whatever is open.
      this.clearFrame()
    }
  }

  // A cached page can contain a stale or empty modal frame. Reset it before
  // Turbo swaps the page in, then load only the modal named by the restored URL.
  // Otherwise Back can reopen a closed modal, or Forward can leave one empty.
  syncModalInRestoredPage(event) {
    if (window.Turbo?.navigator?.currentVisit?.action !== "restore") return

    const frame = event.detail?.newBody?.querySelector("#modal")
    if (!frame) return

    const modal = this.modalFromUrl()
    frame.removeAttribute("src")
    frame.removeAttribute("complete")
    frame.innerHTML = ""
    if (modal) frame.src = this.srcForModal(modal)
  }

  closeStack(event) {
    const depth = Number(event.detail?.depth || 0)
    // A pending Turbo frame response can land after Escape closes the dialog but
    // before history leaves the modal URL. Suppress that stale reconnect.
    this.suppressedModal = {
      type: event.detail?.type,
      id: event.detail?.id,
      expiresAt: Date.now() + 1200
    }

    const restoredHref = this.hrefWithoutModalParams()

    if (depth > 0) {
      // Remove the modal before cloning the live page. The workflow can keep
      // receiving Turbo stream updates while its task dialog is open, so the
      // clean page currently on screen is the authoritative restore fallback.
      this.clearFrame()

      // Only hold the page still for a restore Turbo can serve from memory. A
      // page that opted out of snapshot caching wants the fresh copy it is about
      // to fetch — a task list, say, that has to show the work just completed in
      // the modal — so let that one render as it always has.
      if (this.cachePageForRestore(restoredHref)) this.keepPageAcrossRestore(restoredHref)

      this.preserveScrollAcrossRestore()
      this.normalizeCloseDestination(restoredHref)
      window.history.go(-depth)
    } else {
      window.history.replaceState(this.stateWithoutModal(), "", restoredHref)
      this.clearFrame()
    }
  }

  // A direct modal URL (including one restored by a full reload) can be the
  // first entry in the stack. Closing a later modal would otherwise land on
  // that URL and reopen it. The early popstate listener normalizes only this
  // close's destination, leaving ordinary Back and Forward untouched.
  normalizeCloseDestination(restoredHref) {
    if (pendingCloseRestore) window.clearTimeout(pendingCloseRestore.timeout)
    const timeout = window.setTimeout(() => { pendingCloseRestore = null }, 5000)
    pendingCloseRestore = { href: restoredHref, timeout }
  }

  // Turbo skips the network on a restore visit only when it already holds a
  // snapshot of the page being restored, and it holds none here: the modal's
  // history entry was pushed by this controller, never rendered by Turbo. So
  // dismissing a modal refetches the whole page behind it — progress bar and
  // all — to rebuild what is already on screen. File the page under the URL the
  // restore is heading for and it resolves from memory instead. Always replace
  // an existing entry: a running workflow may have changed since that snapshot
  // was stored, and an old or partial body is not a safe fallback if the render
  // guard expires or is bypassed.
  //
  // Returns whether the restore can be served from cache. `turbo-cache-control:
  // no-cache` pages are left alone: they have said their markup goes stale, and
  // quietly caching them would hand that stale copy to a later Back.
  cachePageForRestore(restoredHref) {
    try {
      const cache = window.Turbo?.session?.view?.snapshotCache
      if (!cache) return false

      const location = new URL(restoredHref)
      const snapshot = window.Turbo?.PageSnapshot?.fromElement(document.documentElement)
      if (!snapshot?.isCacheable) return false

      cache.put(location, snapshot.clone())
      return true
    } catch {
      return false
    }
  }

  // Dismissing a modal changes an overlay, not the page — but the history.go()
  // that dismisses it reads to Turbo as a restore visit, and Turbo's default
  // renderer swaps the whole <body> for a cached snapshot of the page already on
  // screen. That rebuild is what makes Escape feel like a reload: every
  // controller reconnects, tables re-initialise, images re-decode, and anything
  // the reader had expanded snaps shut. The page underneath is already the page
  // being restored, so hand Turbo a no-op renderer and keep the live DOM.
  //
  // Scoped to that one visit: only the first render that lands on exactly the
  // URL this close returns to counts. Live workflow refreshes can render before
  // history traversal finishes; those must not consume the restore guard.
  keepPageAcrossRestore(restoredHref) {
    const keepCurrentPage = (event) => {
      if (window.location.href !== restoredHref) return

      window.clearTimeout(timeout)
      document.removeEventListener("turbo:before-render", keepCurrentPage)
      event.detail.render = () => {}
    }

    const timeout = window.setTimeout(() => {
      document.removeEventListener("turbo:before-render", keepCurrentPage)
    }, 5000)

    document.addEventListener("turbo:before-render", keepCurrentPage)
  }

  // Turbo puts back the scroll position it recorded for the restored history
  // entry, but it has none when the entry was never scrolled under Turbo's eye,
  // and it scrolls to the top instead — so pin the reader's place explicitly.
  // Otherwise closing a modal dumps them at the top (or, on the process page, at
  // the bottom of the timeline) instead of where they were reading.
  preserveScrollAcrossRestore() {
    const x = window.scrollX
    const y = window.scrollY

    const restore = () => {
      window.clearTimeout(timeout)
      window.scrollTo(x, y)

      // Controllers that scroll on mount settle a frame or two after
      // `turbo:load`; re-apply so the reader's place is the last word.
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => window.scrollTo(x, y))
      })
    }

    const timeout = window.setTimeout(() => {
      document.removeEventListener("turbo:load", restore)
    }, 5000)

    document.addEventListener("turbo:load", restore, { once: true })
  }

  // Closing a modal and clicking to open it again is a fresh request, not the
  // stale reconnect the close guards against. Capture phase, so the guard is
  // gone before Turbo starts loading the frame.
  allowRequestedOpen(event) {
    if (event.target instanceof Element && event.target.closest('[data-turbo-frame="modal"]')) {
      this.suppressedModal = null
    }
  }

  suppressStaleOpen(event) {
    const suppressed = this.suppressedModal
    if (!suppressed) return

    if (Date.now() > suppressed.expiresAt) {
      this.suppressedModal = null
      return
    }

    if (event.detail?.type === suppressed.type && event.detail?.id === suppressed.id) {
      event.preventDefault()
      this.clearFrame()
    }
  }

  modalFromUrl() {
    const params = new URLSearchParams(window.location.search)

    for (const [type, param] of Object.entries(this.modalParams)) {
      const id = params.get(param)
      if (id) return { type, id }
    }

    return null
  }

  srcForModal(modal) {
    const stateModal = window.history.state?.modal
    const stateSrc = stateModal?.type === modal.type && stateModal?.id === modal.id ? stateModal.src : null
    const sanitized = this.sanitizeFrameSrc(stateSrc)
    if (sanitized) return sanitized

    return this.fallbackSrcForModal(modal)
  }

  sanitizeFrameSrc(src) {
    if (!src) return null

    const url = new URL(src, window.location.origin)
    if (url.origin !== window.location.origin) return null

    return url.pathname + url.search
  }

  fallbackSrcForModal({ type, id }) {
    const encodedId = encodeURIComponent(id)
    const url = new URL(this.fallbackPathForModal(type, encodedId), window.location.origin)

    for (const [key, value] of Object.entries(this.fallbackQueryForModal(type))) {
      url.searchParams.set(key, value)
    }

    return url.pathname + url.search
  }

  fallbackPathForModal(type, encodedId) {
    switch (type) {
      case "attachment":
        return `/paperwork_attachments/${encodedId}/modal`
      case "task":
        return `/tasks/${encodedId}/modal`
      case "contact":
        return `/contacts/${encodedId}/modal`
      default:
        return window.location.pathname + window.location.search
    }
  }

  fallbackQueryForModal(type) {
    const shareToken = this.shareTokenFromCurrentPage
    if (shareToken && ["attachment", "task"].includes(type)) return { share_token: shareToken }

    const taskToken = this.publicTaskTokenFromCurrentPage
    if (taskToken && type === "attachment") return { task_token: taskToken }

    return {}
  }

  get shareTokenFromCurrentPage() {
    const paramToken = new URLSearchParams(window.location.search).get("share_token")
    if (paramToken) return paramToken

    const match = window.location.pathname.match(/^\/s\/([^/]+)\/review\/?$/)
    return match ? decodeURIComponent(match[1]) : null
  }

  get publicTaskTokenFromCurrentPage() {
    const match = window.location.pathname.match(/^\/task\/([^/]+)\/?$/)
    return match ? decodeURIComponent(match[1]) : null
  }

  clearFrame() {
    const frame = this.frame
    if (!frame) return

    frame.removeAttribute("src")
    frame.removeAttribute("complete")
    frame.innerHTML = ""
  }

  hrefWithoutModalParams() {
    const url = new URL(window.location.href)
    this.clearModalParams(url)
    return url.toString()
  }

  clearModalParams(url) {
    for (const param of Object.values(MODAL_PARAMS)) {
      url.searchParams.delete(param)
    }
  }

  stateWithoutModal() {
    const state = { ...(window.history.state || {}) }
    delete state.modal
    delete state.modalStackDepth
    return state
  }

  get modalParams() {
    return MODAL_PARAMS
  }
}
